import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { partnerAPI } from '@/services/PartnerAPI';
import type { Partner } from '@/types/partner';
import type { PartnerProduct } from '@/composables/services/usePartnerProductsAPI';
import { usePartnerAPI } from '@/composables/services/usePartnerAPI';
import { useUserStore } from '@/stores/user';

export const usePartnerStore = defineStore('partner', () => {
    const products = ref<PartnerProduct[]>([]);
    const partnerTeams = ref<any[]>([]);
    const currentPartner = ref<Partner | null>(null);
    const loading = ref(false);
    const loadingTeams = ref(false);
    const loadingCurrentPartner = ref(false);
    const error = ref<string | null>(null);
    const teamsError = ref<string | null>(null);
    const currentPartnerError = ref<string | null>(null);

    // Helper computed properties for partner type checking
    const isServiceProvider = computed(() => {
        return currentPartner.value?.types?.includes('service_provider') ?? false;
    });

    const isStandardPartner = computed(() => {
        return currentPartner.value?.types?.includes('standard') ?? false;
    });

    async function fetchProducts() {
        try {
            loading.value = true;
            error.value = null;
            const { usePartnerProductsAPI } = await import('@/composables/services/usePartnerProductsAPI');
            const partnerProductsAPI = usePartnerProductsAPI();
            const results = await partnerProductsAPI.fetchPartnerProducts({ limit: 1000 });
            // Extract the products array from the result object
            products.value = results?.products || [];
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to fetch products';
            console.error('Error fetching products:', err);
            // Ensure products is always an array even on error
            products.value = [];
        } finally {
            loading.value = false;
        }
    }

    async function fetchPartnerTeams(ecosystem: boolean = false) {
        try {
            loadingTeams.value = true;
            teamsError.value = null;
            
            const api = usePartnerAPI();
            const response = await api.fetchPartnerTeams(ecosystem);
            
            if (response.success) {
                let teams = response.pl__org_partners_teams || [];
                
                if (!ecosystem) {
                    // Get current user's organization from user store
                    const userStore = useUserStore();
                    const currentOrgId = userStore.userTeams?.[0]?.team_id?.substring(0, 3) || userStore.userTeams?.[0]?.substring(0, 3);
                    
                    // Filter teams to only include those from current organization
                    teams = teams.filter((team: any) => team.val.substring(0, 3) === currentOrgId);
                }
                
                partnerTeams.value = teams;
            } else {
                throw new Error('Failed to fetch partner teams');
            }
        } catch (err) {
            teamsError.value = err instanceof Error ? err.message : 'Failed to fetch partner teams';
            console.error('Error fetching partner teams:', err);
        } finally {
            loadingTeams.value = false;
        }
    }

    /**
     * Wait for user data to be available
     * This prevents race conditions during app initialization where components
     * mount before user data is populated from setTimeout in auth store
     * Max wait time: 20 attempts * 50ms = 1 second
     */
    async function waitForUserData(maxAttempts = 20, delayMs = 50): Promise<string> {
        const userStore = useUserStore();
        
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const partnerId = userStore.userData?.object_id;
            if (partnerId) {
                return partnerId;
            }
            
            // Wait before next attempt
            await new Promise(resolve => setTimeout(resolve, delayMs));
        }
        
        throw new Error('No partner ID found in user data - user data not loaded after maximum wait time (1000ms)');
    }

    async function fetchCurrentPartner() {
        try {
            loadingCurrentPartner.value = true;
            currentPartnerError.value = null;
            
            // Wait for user data to be available (with timeout)
            const partnerId = await waitForUserData();
            
            const api = usePartnerAPI();
            const response = await api.fetchCurrentPartner(partnerId);
            
            if (response.success && response.partners.results.length > 0) {
                currentPartner.value = response.partners.results[0];
            } else {
                throw new Error('No partner data found');
            }
        } catch (err) {
            currentPartnerError.value = err instanceof Error ? err.message : 'Failed to fetch current partner';
            console.error('Error fetching current partner:', err);
        } finally {
            loadingCurrentPartner.value = false;
        }
    }

    return {
        products,
        partnerTeams,
        currentPartner,
        loading,
        loadingTeams,
        loadingCurrentPartner,
        error,
        teamsError,
        currentPartnerError,
        isServiceProvider,
        isStandardPartner,
        fetchProducts,
        fetchPartnerTeams,
        fetchCurrentPartner
    };
}); 